/**
 * VideoRenewalView: /video-renewal 프레젠테이션 컴포넌트.
 *
 * VideoView.tsx와 동일한 레이아웃이나 다음이 다르다:
 * - Fabric.js 핸들러(onSendCanvasData 등) 없음
 * - screenMode (GRID/SIDE/PRESENTER) 기반 CSS 클래스
 * - notosans_font 클래스 (v1 WrapHOC 패턴)
 * - Phase 7에서 ExcalidrawBoard 추가 예정
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import LUXDialog from '@/components/Dialog';
import RenewalDefaultSetting from '@/pages/PreMeeting/RenewalDefaultSetting';
import ContentHeader from '@/components/Footer/ContentHeader';
import Footer from '@/components/Footer/Footer';
import { isRecorderMode } from '@/utils/recorderMode';
import SVContentRenewal from '@/components/SVContent/SVContentRenewal';
import SVSideBar from '@/components/SVSideBar/SVSideBar';
import ParticipantAudio from '@/components/ParticipantAudio/ParticipantAudio';
import DeviceSettingsModal from '@/components/DeviceSettingsModal/DeviceSettingsModal';
import FloorControl from '@/components/FloorControl/FloorControl';
import { RenewalDialogProvider } from '@/components/Dialog';
import WrapLayout from '@/components/WrapLayout/WrapLayout';
import VirtualBackgroundDialog from '@/components/VirtualBackgroundDialog/VirtualBackgroundDialog';
import VoiceNote from '@/components/VoiceNote/VoiceNote';
import NotiCardWrap from '@/components/NotiCardWrap/NotiCardWrap';
import GlobalNotification from '@/components/GlobalNotification/GlobalNotification';
import GlobalToast from '@/components/GlobalToast/GlobalToast';
import AiSummaryBanner from '@/components/AiSummaryBanner/AiSummaryBanner';

const NOOP = () => {};

interface VideoRenewalViewProps {
  localUserId: string;
  onJoinRoom?: () => void;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onHangup: () => void;
  onSendMessage: (message: string) => void;
  onKickParticipant: (userId: string) => void;
  onGrantMaster?: (userId: string) => boolean | Promise<boolean>;
  onClearMaster?: (userId: string) => boolean | Promise<boolean>;
  onSelectParticipant: (userId: string | null) => void;
  onRequestFloor?: () => void;
  onGrantFloor?: (userId: string) => void;
  onGrantFloorTarget?: (userId: string, type: 'accept' | 'reject') => void;
  onStopFloor?: () => void;
  onToggleRecording?: () => void;
  onChangeDevice?: (type: string, deviceId: string) => void;
  onResponseJoinRequest?: (userId: string, status: 'allow' | 'deny') => void;
  onEndMeetingForAll?: () => void;
}

const VideoRenewalView: React.FC<VideoRenewalViewProps> = ({
  localUserId,
  onJoinRoom,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onHangup,
  onSendMessage,
  onKickParticipant,
  onGrantMaster,
  onClearMaster,
  onSelectParticipant,
  onRequestFloor,
  onGrantFloor,
  onGrantFloorTarget,
  onStopFloor,
  onToggleRecording,
  onChangeDevice,
  onResponseJoinRequest,
  onEndMeetingForAll,
}) => {
  const dispatch = useAppDispatch();
  const conferenceStatus = useAppSelector((s) => s.conference.status);
  const conferenceErrorMessage = useAppSelector((s) => s.conference.errorMessage);
  const localIsConnecting = useAppSelector((s) => s.local.isConnecting);
  const localUserIdValue = useAppSelector((s) => s.local.userId);
  const localRoomName = useAppSelector((s) => s.local.roomName);
  const uiIsConnectionDropped = useAppSelector((s) => s.ui.isConnectionDropped);
  const uiAlerts = useAppSelector((s) => s.ui.alerts);
  const uiOpenSideBar = useAppSelector((s) => s.ui.openSideBar);
  // recorder(Jibri) 모드: 녹화 화면에는 UI 크롬(헤더/툴바 등)을 렌더하지 않는다.
  const recorder = isRecorderMode();

  const handleOpenSideChat = useCallback(() => {
    dispatch(uiActions.setOpenSideBar(true));
    dispatch(uiActions.setSidebarContent('chat'));
  }, [dispatch]);

  const onHangupRef = useRef(onHangup);
  onHangupRef.current = onHangup;

  useEffect(() => {
    if (uiIsConnectionDropped) {
      // recorder 모드: 모달을 녹화 화면에 띄우지 않고 조용히 재입장을 시도한다.
      if (recorder) {
        window.location.reload();
        return;
      }
      LUXDialog.confirm(
        ['네트워크가 불안정하여 연결이 종료되었습니다.', '네트워크 상태 확인 후 회의에 다시 참석해 주세요.'],
        {
          type: 'warning',
          title: '회의 연결 종료',
          confirmButtonLabel: '재입장',
          cancelButtonLabel: '나가기',
          confirmCallback: () => window.location.reload(),
          cancelCallback: () => onHangupRef.current(),
          onCloseCallback: () => onHangupRef.current(),
        },
      );
    }
  }, [uiIsConnectionDropped]);

  // ─── 연결 중 / 회의 전 기본 설정 ────────────────────────────────────────
  if (conferenceStatus === 'idle' || conferenceStatus === 'connecting' || localIsConnecting || conferenceStatus === 'setup') {
    return <RenewalDefaultSetting onJoinRoom={onJoinRoom ?? NOOP} />;
  }

  // ─── 연결 실패 ────────────────────────────────────────────────────────────
  if (conferenceStatus === 'failed') {
    return (
      <div className="rtc_wrap notosans_font">
        <div className="rtcCustoms_failedScreen">
          <div className="rtcCustoms_failedScreen__title">회의 연결에 실패했습니다</div>
          {conferenceErrorMessage && (
            <div className="rtcCustoms_failedScreen__message">{conferenceErrorMessage}</div>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rtcCustoms_failedScreen__retryBtn"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // ─── 종료 중 ─────────────────────────────────────────────────────────────
  if (conferenceStatus === 'leaving') {
    return (
      <div className="rtc_wrap notosans_font">
        <div className="rtcCustoms_leavingScreen">
          회의를 종료하고 있습니다...
        </div>
      </div>
    );
  }

  // ─── 메인 회의 화면 ───────────────────────────────────────────────────────
  // v1 구조: WrapHOC(.rtc.rtc_new.rtc_new3.wrap.notosans_font)
  //   ├─ #header.header (Footer 하단 툴바)
  //   ├─ NotiCardWrap
  //   └─ .content.content--viewer
  //       ├─ .viewerHeader (ContentHeader 상단 제목바)
  //       ├─ DivisionScreen/SideScreen/PresenterScreen
  //       └─ .sidebar.new2 (SVSideBar)
  return (
    <RenewalDialogProvider className="rtcCustoms_fullHeight">
      <WrapLayout>
        {/* 사용자 정보 히든 입력 (포탈 공통 컴포넌트 연동용) */}
        <input type="hidden" id="h_portal_id" value={localUserIdValue || ''} />
        <input type="hidden" id="h_selected_company_name_kr" value="" />
        <input type="hidden" id="h_selected_company_no" value="" />



        {/* 알림 토스트 */}
        {uiAlerts.length > 0 && (
          <div className="rtcCustoms_alertContainer">
            {uiAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`rtcCustoms_alertItem rtcCustoms_alertItem--${alert.type === 'error' ? 'error' : alert.type === 'warning' ? 'warning' : alert.type === 'success' ? 'success' : 'info'}`}
              >
                <span>{alert.message}</span>
                <button
                  type="button"
                  onClick={() => dispatch(uiActions.dismissAlert(alert.id))}
                  className="rtcCustoms_alertDismiss"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 하단 툴바 — v1: #header.header (position: absolute; bottom: 0) */}
        {/* recorder 모드에서는 컨트롤 툴바를 녹화 화면에 넣지 않는다. */}
        {!recorder && (
          <Footer
            onToggleAudio={onToggleAudio}
            onToggleVideo={onToggleVideo}
            onToggleScreenShare={onToggleScreenShare}
            onHangup={onHangup}
            onOpenSettings={() => dispatch(uiActions.setSettingsModalOpen(true))}
            onRequestFloor={onRequestFloor}
            onToggleRecording={onToggleRecording}
            onChangeDevice={onChangeDevice}
            onEndMeetingForAll={onEndMeetingForAll}
          />
        )}

        {/* 전역 상단 알림 — recorder 모드에서는 녹화 화면에 넣지 않는다. */}
        {!recorder && <GlobalNotification />}

        {/* 알림 카드 (참석요청/발언권/채팅) — recorder 모드에서는 숨김 */}
        {!recorder && (
          <NotiCardWrap
            onResponseJoinRequest={onResponseJoinRequest}
            onGrantFloorTarget={onGrantFloorTarget}
            onOpenSideChat={handleOpenSideChat}
          />
        )}

        {/* 메인 콘텐츠 영역 — v1: .content.content--viewer (사이드바 열림 시 showSidebar 추가) */}
        <div className={`content content--viewer${uiOpenSideBar ? ' showSidebar' : ''}`}>
          {/* 상단 제목바 — v1: .viewerHeader (absolute positioned) */}
          {/* recorder 모드에서는 제목바를 녹화 화면에 넣지 않는다. */}
          {!recorder && (
            <ContentHeader roomName={localRoomName} onStopRecording={onToggleRecording} />
          )}

          {/* AI 요약 알림 배너 (우측 상단 플로팅) — recorder 모드에서는 숨김 */}
          {!recorder && <AiSummaryBanner />}

          {/* 비디오 영역 — GRID/SIDE/PRESENTER 모드 */}
          <SVContentRenewal
            localUserId={localUserId}
            onSelectParticipant={onSelectParticipant}
          />

          {/* 전역 우하단 토스트 — recorder 모드에서는 녹화 화면에 넣지 않는다. */}
          {!recorder && <GlobalToast />}

          {/* 사이드바 — v1: .sidebar.new2 (inside .content) */}
          <SVSideBar
            localUserId={localUserId}
            onSendMessage={onSendMessage}
            onKickParticipant={onKickParticipant}
            onGrantMaster={onGrantMaster}
            onClearMaster={onClearMaster}
            onToggleAudio={onToggleAudio}
            onToggleVideo={onToggleVideo}
            onResponseJoinRequest={onResponseJoinRequest}
          />
        </div>

        <FloorControl
          onRequestFloor={onRequestFloor ?? NOOP}
          onGrantFloor={onGrantFloor ?? NOOP}
          onGrantFloorTarget={onGrantFloorTarget ?? NOOP}
          onStopFloor={onStopFloor ?? NOOP}
        />

        {/* 오디오 재생 */}
        <ParticipantAudio />

        {/* 모달/오버레이 레이어 */}
        <DeviceSettingsModal onChangeDevice={onChangeDevice} />
        <VirtualBackgroundDialog />
        <VoiceNote />
        {/* <Caption /> */}
      </WrapLayout>
    </RenewalDialogProvider>
  );
};

export default VideoRenewalView;
